import * as React from "react"
import { Box } from "@chakra-ui/react"
import Header from "./Header"
import Footer from "./Footer"

type LayoutProps = {
  children: React.ReactNode
  pathname?: string
}

// Post-conversion pages are self-contained single screens — the marketing
// footer would only add height (forcing a page scrollbar) and a white seam
// below the confirmation's ink zone. Suppress it there, and paint the page
// background ink so the area below the (shorter) confirmation section reads as
// a seamless continuation of its ink horizon rather than a white strip.
const CONFIRMATION_INK = "#1E2740"
const isChromeless = (pathname?: string): boolean =>
  Boolean(pathname && pathname.startsWith("/signup/confirmed"))

// PM-branded owner pages carry the PM's own header and footer ("powered by
// Spot"), so the Spot marketing chrome would put two brands on one page.
const PM_BRANDED_PREFIXES = ["/pm/meadow-gardens"]
const isPmBranded = (pathname?: string): boolean =>
  Boolean(pathname && PM_BRANDED_PREFIXES.some((p) => pathname.startsWith(p)))

// Authenticated app surfaces carry their own app chrome (AppBar + TabNav), so
// the marketing nav/footer don't belong here — they stacked a third nav layer
// on logged-in users and bounced them back to the marketing home. Suppress both
// and let these pages own their top bar.
const APP_SHELL_PREFIXES = ["/dashboard", "/admin", "/onboarding"]
const isAppShell = (pathname?: string): boolean =>
  Boolean(pathname && APP_SHELL_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`)))

const Layout: React.FC<LayoutProps> = ({ children, pathname }) => {
  if (isPmBranded(pathname)) {
    return <Box as="main">{children}</Box>
  }
  if (isAppShell(pathname)) {
    return (
      <Box bg="bg" minH="100vh" display="flex" flexDirection="column">
        <Box as="main" flex="1">
          {children}
        </Box>
      </Box>
    )
  }
  const hideFooter = isChromeless(pathname)
  return (
    <Box
      bg={hideFooter ? CONFIRMATION_INK : "bg"}
      minH="100vh"
      display="flex"
      flexDirection="column"
    >
      <Header />
      <Box as="main" flex="1">
        {children}
      </Box>
      {!hideFooter && <Footer />}
    </Box>
  )
}

export default Layout
